import Auth from '@aws-amplify/auth';
import awsConfig from '../aws-exports';
import { Audit, AuditInput, AuditListFilter, AuditUpdateInput, PaginatedAudits } from './auditTypes';

const { audit_api_url: auditApiUrl } = awsConfig;

const authorizedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
	const currentSession = await Auth.currentSession();
	const jwtToken = currentSession.getIdToken().getJwtToken();

	const response = await fetch(url, {
		...options,
		headers: {
			...options.headers,
			authorization: `Bearer ${jwtToken}`,
			'content-type': 'application/json'
		}
	});

	if (!response.ok) {
		const errorBody = await response.json().catch(() => ({ message: response.statusText }));
		throw new Error(errorBody.message ?? `Request to ${url} failed with status ${response.status}`);
	}

	return response;
};

export const createAuditRecord = async (audit: AuditInput): Promise<Audit> => {
	const response = await authorizedFetch(auditApiUrl, { method: 'POST', body: JSON.stringify(audit) });
	return response.json();
};

export const updateAuditRecord = async (audit: AuditUpdateInput): Promise<Audit> => {
	const response = await authorizedFetch(auditApiUrl, { method: 'PUT', body: JSON.stringify(audit) });
	return response.json();
};

export const deleteAuditRecord = async (id: number): Promise<{ id: number }> => {
	const response = await authorizedFetch(`${auditApiUrl}?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
	return response.json();
};

export const getAuditRecord = async (id: number): Promise<Audit> => {
	const response = await authorizedFetch(`${auditApiUrl}?id=${encodeURIComponent(id)}`, { method: 'GET' });
	return response.json();
};

export const listAuditRecords = async (filter: AuditListFilter): Promise<PaginatedAudits> => {
	const queryParams = Object.entries(filter)
		.filter(([, value]) => value !== undefined && value !== null && value !== '')
		.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
		.join('&');

	const response = await authorizedFetch(`${auditApiUrl}${queryParams ? `?${queryParams}` : ''}`, { method: 'GET' });
	return response.json();
};
